import { Board } from "./board";
import { Walkthrough } from "./walkthrough";
import { Highlight, Mode, Puzzle, RC, Step } from "./types";
import { h, idx, peers } from "./util";
import { applyProgress, clearProgress, loadProgress, markDone, saveProgress } from "./storage";
import { TECHNIQUE_INFO } from "./technique-info";

const AMAZON = "https://www.amazon.com/dp/B0H57ZCNR1";
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** The interactive board. One class, two modes: `play` (the visitor solves, with
 *  hints) and `stuck` (auto-step through the baked solution with reasons). */
export class Game {
  private board: Board;
  private wt?: Walkthrough; // stuck mode only
  private cells: HTMLButtonElement[] = [];
  private loopSvg!: SVGElement; // overlay: loops drawn around found words
  private wordEls = new Map<string, HTMLElement>();
  private keyEls = new Map<string, HTMLButtonElement>();
  private rackEls = new Map<string, HTMLElement>();

  private statusEl!: HTMLElement;
  private walkEl!: HTMLElement;
  private walkStepEl!: HTMLElement;
  private walkReasonEl!: HTMLElement;
  private walkDetailEl!: HTMLElement;
  private walkLegendEl!: HTMLElement;
  private walkDefEl!: HTMLElement;
  private winEl!: HTMLElement;
  private segNormal?: HTMLButtonElement;
  private segCand?: HTMLButtonElement;

  private sel: number | null = null;
  private pencilMode = false;
  private autoCandidates = false; // "auto": keep pencil candidates in sync as cells change
  private activeLetter: string | null = null;
  private hintCells = new Set<number>();
  private roles: Record<string, Set<number>> = {}; // stuck: current step's highlight cells by role
  private flaggedWrong = new Set<number>();
  // Undo (play): each entry is a batch of cell snapshots (value + pencil) taken
  // before a move. A normal move is one cell; Prefill is many, undone together.
  private undoStack: Array<Array<{ i: number; value: string | null; pencil: Set<string> }>> = [];
  private autoTimer: number | null = null;
  private won = false;

  constructor(private mount: HTMLElement, private puzzle: Puzzle, private mode: Mode) {
    this.board = new Board(puzzle);
    this.build();
    if (mode === "stuck") {
      this.wt = new Walkthrough(this.board, puzzle.steps, puzzle.alphabet);
      // Optional deep link to a specific step: /stuck/<code>/?step=N
      const want = Number(new URLSearchParams(window.location.search).get("step") || 0);
      if (want > 0) this.wt.goto(Math.min(want, this.wt.total));
      this.updateWalk();
    } else {
      const pr = loadProgress(puzzle.code);
      if (pr) applyProgress(this.board, pr);
      this.paint();
    }
  }

  // ---- construction ----
  private build(): void {
    const p = this.puzzle;
    const root = h("div", { class: `swsgame mode-${this.mode}` });

    const meta = h("div", { class: "sws-metarow" });
    meta.append(h("span", { class: `sws-chip diff-${p.difficulty}`, text: cap(p.difficulty) }));
    if (p.book) meta.append(h("span", { class: "sws-chip", text: p.book }));
    if (p.number) meta.append(h("span", { class: "sws-chip", text: `Puzzle ${p.number}` }));
    root.append(meta);

    // grid
    const grid = h("div", { class: "sws-grid", role: "grid", "aria-label": "Sudoku word search grid" });
    for (let i = 0; i < 81; i++) {
      const r = Math.floor(i / 9);
      const c = i % 9;
      const classes = ["sws-cell"];
      if (this.board.given[i]) classes.push("is-given");
      if (c === 2 || c === 5) classes.push("bx-right");
      if (r === 2 || r === 5) classes.push("bx-bottom");
      const cell = h("button", {
        class: classes.join(" "),
        type: "button",
        role: "gridcell",
        "aria-label": `row ${r + 1} column ${c + 1}`,
        dataset: { i: String(i) },
      }) as HTMLButtonElement;
      this.cells.push(cell);
      grid.append(cell);
    }
    grid.addEventListener("click", (e) => {
      const t = (e.target as HTMLElement).closest(".sws-cell") as HTMLElement | null;
      if (t && t.dataset.i) this.onCellClick(Number(t.dataset.i));
    });
    // An SVG overlay (cell-unit coords) that draws a loop around each found word.
    const NS = "http://www.w3.org/2000/svg";
    const loops = document.createElementNS(NS, "svg");
    loops.setAttribute("class", "sws-loops");
    loops.setAttribute("viewBox", "0 0 9 9");
    loops.setAttribute("preserveAspectRatio", "none");
    loops.setAttribute("aria-hidden", "true");
    this.loopSvg = loops;
    const gridwrap = h("div", { class: "sws-gridwrap" });
    gridwrap.append(grid, loops);

    this.statusEl = h("p", { class: "sws-status", "aria-live": "polite" });
    const aside = h("aside", { class: "sws-aside" });
    if (this.mode === "play") {
      // Input lives beside the grid: the entry mode, the letter pad (the
      // alphabet, as input), the action row, then the candidate tools (Prefill /
      // Clear / auto) tucked under the actions.
      aside.append(this.buildModeSeg(), this.buildKeypad(), this.buildControls());
      aside.append(this.buildCandidateControls(), this.statusEl);
      // Reference zone (after a gap): a labelled "Alphabet" rack — a clear
      // at-a-glance indicator that greys out completed letters — then the words.
      aside.append(h("p", { class: "sws-words-title sws-words-gap", text: "Alphabet" }), this.buildAlphabetRack());
      aside.append(h("p", { class: "sws-words-title", text: "Hidden words" }), this.buildWordList());
    } else {
      // Stuck (walkthrough): a read-only alphabet reference (there's no input),
      // the word list, then the step controls and the walkthrough panel.
      aside.append(h("p", { class: "sws-words-title", text: "Alphabet" }), this.buildAlphabetRack());
      aside.append(h("p", { class: "sws-words-title", text: "Hidden words" }), this.buildWordList());
      aside.append(this.buildControls(), this.statusEl);
    }
    this.walkEl = h("div", { class: "sws-walk", hidden: this.mode === "play" ? "" : null });
    this.walkStepEl = h("p", { class: "sws-walk-step" });
    this.walkReasonEl = h("p", { class: "sws-walk-reason" });
    this.walkDetailEl = h("p", { class: "sws-walk-detail" });
    this.walkLegendEl = h("ul", { class: "sws-legend" });
    this.walkDefEl = h("div", { class: "sws-tech-def" });
    this.walkEl.append(this.walkStepEl, this.walkReasonEl, this.walkDetailEl, this.walkLegendEl, this.walkDefEl);
    if (this.mode === "play") {
      // The hint shows in this panel; let the player dismiss it.
      const close = h("button", { class: "sws-walk-close", type: "button", text: "×", "aria-label": "Hide hint" }) as HTMLButtonElement;
      close.addEventListener("click", () => this.hideHint());
      this.walkEl.append(close);
    }
    aside.append(this.walkEl);

    const main = h("div", { class: "sws-main" }, [h("div", { class: "sws-board" }, [gridwrap]), aside]);
    root.append(main);

    this.winEl = h("div", { class: "sws-win", hidden: "" });
    root.append(this.winEl);

    this.mount.replaceChildren(root);

    if (this.mode === "play") this.wireKeyboard();
  }

  private buildControls(): HTMLElement {
    const wrap = h("div", { class: "sws-controls" });
    if (this.mode === "play") {
      wrap.append(
        this.btn("Undo", () => this.undo()),
        this.btn("Erase", () => this.erase()),
        this.btn("Check", () => this.check()),
        this.btn("Hint", () => this.hint()),
        this.btn("Reset", () => this.reset()),
      );
    } else {
      wrap.append(
        this.btn("◀ Back", () => this.step(-1)),
        this.btn("Next ▶", () => this.step(1), true),
        this.btn("Auto-play", () => this.toggleAuto()),
        this.btn("↺ Restart", () => this.restart()),
      );
    }
    return wrap;
  }

  private btn(label: string, on: () => void, primary = false): HTMLButtonElement {
    const b = h("button", { class: `sws-btn${primary ? " primary" : ""}`, type: "button", text: label }) as HTMLButtonElement;
    b.addEventListener("click", on);
    return b;
  }

  /** The letter pad — nine keys that are the input (and grey out when a letter
   *  is complete). Laid out 5+4 across two rows on desktop (see CSS). */
  private buildKeypad(): HTMLElement {
    const pad = h("div", { class: "sws-keypad" });
    for (const L of this.puzzle.alphabet) {
      const k = h("button", { class: "sws-key", type: "button", text: L }) as HTMLButtonElement;
      k.addEventListener("click", () => this.onKey(L));
      this.keyEls.set(L, k);
      pad.append(k);
    }
    return pad;
  }

  /** The entry-mode segmented control (Normal | Candidates), above the pad. */
  private buildModeSeg(): HTMLElement {
    const seg = h("div", { class: "sws-seg sws-mode" });
    this.segNormal = this.segBtn("Normal", false);
    this.segCand = this.segBtn("Candidates", true);
    seg.append(this.segNormal, this.segCand);
    return seg;
  }

  /** Candidate tools under the action row: prefill / clear all pencil marks, and
   *  an "auto" checkbox that keeps them in sync with the grid as you play. */
  private buildCandidateControls(): HTMLElement {
    const row = h("div", { class: "sws-cands" });
    const prefill = h("button", {
      class: "sws-btn sws-cand-btn",
      type: "button",
      text: "Prefill candidates",
      title: "Pencil in every candidate not already used in each empty cell's row, column, or box",
    }) as HTMLButtonElement;
    prefill.addEventListener("click", () => this.prefillCandidates());
    const clear = h("button", {
      class: "sws-btn sws-cand-btn",
      type: "button",
      text: "Clear candidates",
      title: "Erase all pencil marks",
    }) as HTMLButtonElement;
    clear.addEventListener("click", () => this.clearCandidates());
    const cb = h("input", { type: "checkbox", class: "sws-auto-cb" }) as HTMLInputElement;
    cb.addEventListener("change", () => this.setAutoCandidates(cb.checked));
    const auto = h(
      "label",
      { class: "sws-auto", title: "Keep candidates updated automatically as you enter or delete letters" },
      [cb, document.createTextNode(" auto")],
    );
    row.append(prefill, clear, auto);
    return row;
  }

  /** A labelled, read-only alphabet rack (greys out completed letters). */
  private buildAlphabetRack(): HTMLElement {
    const rack = h("div", { class: "sws-rack" });
    for (const L of this.puzzle.alphabet) {
      const cell = h("span", { class: "sws-rack-cell", text: L });
      this.rackEls.set(L, cell);
      rack.append(cell);
    }
    return rack;
  }

  /** The "Hidden words" checklist — one builder for both modes. */
  private buildWordList(): HTMLElement {
    const words = h("ul", { class: "sws-words" });
    for (const w of this.puzzle.words) {
      // In play mode this list is a find-it-yourself checklist: clicking a word
      // must NOT reveal where it is. Only stuck mode flashes its grid location.
      const li = h("li", {
        class: "sws-word",
        text: w,
        dataset: { word: w },
        title: this.mode === "stuck" ? "Highlight in grid" : null,
      });
      if (this.mode === "stuck") li.addEventListener("click", () => this.flashWord(w));
      this.wordEls.set(w, li);
      words.append(li);
    }
    return words;
  }

  /** One side of the entry-mode segmented control; `candidates` = pencil mode. */
  private segBtn(label: string, candidates: boolean): HTMLButtonElement {
    const b = h("button", {
      class: `sws-seg-btn${candidates === this.pencilMode ? " is-on" : ""}`,
      type: "button",
      text: label,
    }) as HTMLButtonElement;
    b.addEventListener("click", () => this.setPencilMode(candidates));
    return b;
  }

  // ---- play interaction ----
  private onCellClick(i: number): void {
    if (this.mode !== "play") return;
    this.sel = i;
    this.activeLetter = this.board.value[i];
    this.paint();
  }

  private onKey(letter: string): void {
    if (this.mode !== "play" || this.won) return;
    this.activeLetter = letter;
    if (this.sel == null) {
      this.paint(); // no cell chosen yet — just highlight that letter everywhere
      return;
    }
    const i = this.sel;
    if (this.board.given[i]) return;
    this.commit(() => {
      if (this.pencilMode) this.board.togglePencil(i, letter);
      else this.board.set(i, letter);
    }, true);
  }

  private erase(): void {
    if (this.mode !== "play" || this.won || this.sel == null) return;
    const i = this.sel;
    if (this.board.given[i] || (!this.board.value[i] && this.board.pencil[i].size === 0)) return;
    this.commit(() => this.board.set(i, null), true);
  }

  private setPencilMode(candidates: boolean): void {
    this.pencilMode = candidates;
    this.segNormal?.classList.toggle("is-on", !candidates);
    this.segCand?.classList.toggle("is-on", candidates);
  }

  /** Candidates for an empty cell: alphabet letters not already used by a peer. */
  private candidatesFor(i: number): Set<string> {
    const used = new Set<string>();
    for (const j of peers(i)) {
      const v = this.board.value[j];
      if (v) used.add(v);
    }
    return new Set(this.puzzle.alphabet.filter((L) => !used.has(L)));
  }

  /** Pencil the current candidates into every empty cell. */
  private recomputeCandidates(): void {
    for (let i = 0; i < 81; i++) {
      if (this.board.given[i] || this.board.value[i]) continue;
      this.board.setPencil(i, this.candidatesFor(i));
    }
  }

  private sameSet(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const x of a) if (!b.has(x)) return false;
    return true;
  }

  /** Run a board mutation as one undoable step: snapshot the fillable cells, apply
   *  it (plus an auto-candidate refresh when `syncAuto` and auto is on), and push
   *  the cells that actually changed as a single Undo batch. */
  private commit(act: () => void, syncAuto = false): void {
    const before = this.board.value.map((v, i) =>
      this.board.given[i] ? null : { value: v, pencil: new Set(this.board.pencil[i]) },
    );
    act();
    if (syncAuto && this.autoCandidates) this.recomputeCandidates();
    const batch: Array<{ i: number; value: string | null; pencil: Set<string> }> = [];
    for (let i = 0; i < 81; i++) {
      const b = before[i];
      if (!b) continue;
      if (b.value !== this.board.value[i] || !this.sameSet(b.pencil, this.board.pencil[i])) {
        batch.push({ i, value: b.value, pencil: b.pencil });
      }
    }
    if (batch.length) this.undoStack.push(batch);
    this.afterChange();
  }

  private prefillCandidates(): void {
    if (this.mode !== "play" || this.won) return;
    this.commit(() => this.recomputeCandidates());
  }

  private clearCandidates(): void {
    if (this.mode !== "play" || this.won) return;
    this.commit(() => {
      for (let i = 0; i < 81; i++) {
        if (!this.board.given[i] && !this.board.value[i]) this.board.setPencil(i, new Set());
      }
    });
  }

  /** Toggle "auto" candidate sync; turning it on prefills immediately. */
  private setAutoCandidates(on: boolean): void {
    this.autoCandidates = on;
    if (on) this.commit(() => this.recomputeCandidates());
  }

  private undo(): void {
    if (this.mode !== "play" || this.won) return;
    const last = this.undoStack.pop();
    if (!last || !last.length) {
      this.setStatus("Nothing to undo.", "");
      return;
    }
    for (const s of last) this.board.restore(s.i, s.value, s.pencil);
    this.sel = last[0].i;
    this.activeLetter = this.board.value[last[0].i];
    this.flaggedWrong.clear();
    this.hintCells.clear();
    this.setStatus("", "");
    this.paint();
    saveProgress(this.puzzle.code, this.board);
  }

  private wireKeyboard(): void {
    document.addEventListener("keydown", (e) => {
      if (this.mode !== "play" || this.won) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        this.undo();
        e.preventDefault();
        return;
      }
      if (this.sel == null) return;
      const k = e.key;
      if (k === "Backspace" || k === "Delete" || k === "0") {
        this.erase();
        e.preventDefault();
      } else if (k === "ArrowUp" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowRight") {
        this.moveSel(k);
        e.preventDefault();
      } else if (k.length === 1) {
        const L = k.toUpperCase();
        if (this.puzzle.alphabet.includes(L)) {
          this.onKey(L);
          e.preventDefault();
        }
      }
    });
  }

  private moveSel(arrow: string): void {
    if (this.sel == null) return;
    let r = Math.floor(this.sel / 9);
    let c = this.sel % 9;
    if (arrow === "ArrowUp") r = (r + 8) % 9;
    else if (arrow === "ArrowDown") r = (r + 1) % 9;
    else if (arrow === "ArrowLeft") c = (c + 8) % 9;
    else c = (c + 1) % 9;
    this.sel = idx(r, c);
    this.activeLetter = this.board.value[this.sel];
    this.paint();
  }

  private afterChange(): void {
    this.flaggedWrong.clear();
    this.hintCells.clear();
    this.setStatus("", ""); // any Check / hint message is stale once the grid changes
    this.paint();
    saveProgress(this.puzzle.code, this.board);
    if (this.board.isComplete()) this.win();
  }

  private check(): void {
    const wrong = this.board.wrongCells();
    this.flaggedWrong = new Set(wrong);
    const filled = this.board.filledCount();
    if (wrong.length === 0) {
      this.setStatus(filled === 81 ? "Solved!" : "No mistakes so far — keep going.", "ok");
    } else {
      this.setStatus(`${wrong.length} cell${wrong.length > 1 ? "s" : ""} ${wrong.length > 1 ? "don't" : "doesn't"} match the solution.`, "bad");
    }
    this.paint();
  }

  private hint(): void {
    // The earliest cell any step fills that the player hasn't filled correctly.
    let step: Step | undefined;
    let cell: RC | undefined;
    for (const st of this.puzzle.steps) {
      const f = (st.fills || []).find((g) => this.board.value[idx(g.cell[0], g.cell[1])] !== g.letter);
      if (f) {
        step = st;
        cell = f.cell;
        break;
      }
    }
    if (!step || !cell) {
      this.setStatus("Every cell is already correct.", "ok");
      return;
    }
    const i = idx(cell[0], cell[1]);
    this.sel = i;
    this.hintCells = new Set([i]);
    this.activeLetter = null;
    this.walkEl.hidden = false;
    this.walkStepEl.replaceChildren(document.createTextNode("Hint · "), this.techniqueLabel(step.technique, step.reason));
    this.walkReasonEl.textContent = step.reason;
    this.walkDetailEl.textContent = "";
    this.walkLegendEl.replaceChildren();
    this.showDef(step.technique);
    this.setStatus(`Look at row ${cell[0] + 1}, column ${cell[1] + 1}.`, "");
    this.paint();
  }

  private hideHint(): void {
    this.walkEl.hidden = true;
    this.hintCells.clear();
    this.paint();
  }

  private reset(): void {
    if (!confirm("Clear all your entries for this puzzle?")) return;
    this.board.clearAll();
    clearProgress(this.puzzle.code);
    this.undoStack = [];
    this.won = false;
    this.flaggedWrong.clear();
    this.hintCells.clear();
    this.walkEl.hidden = true;
    this.winEl.hidden = true;
    this.setStatus("", "");
    this.paint();
  }

  private win(): void {
    this.won = true;
    markDone(this.puzzle.number);
    this.setStatus("Solved!", "ok");
    this.winEl.replaceChildren(
      h("h3", { text: "Solved! 🎉" }),
      h("p", { text: "Every row, column, and box holds all nine letters — and every hidden word is in place." }),
      h("div", { class: "hero-cta" }, [
        h("a", { class: "btn", href: "/play/", text: "Play another" }),
        h("a", { class: "btn btn-amazon", href: AMAZON, rel: "nofollow noopener", target: "_blank", text: "Get the paperback" }),
      ]),
    );
    this.winEl.hidden = false;
    this.paint();
  }

  // ---- stuck walkthrough ----
  private step(dir: number): void {
    this.stopAuto();
    if (dir > 0) this.wt!.next();
    else this.wt!.prev();
    this.updateWalk();
  }

  private restart(): void {
    this.stopAuto();
    this.wt!.reset();
    this.winEl.hidden = true;
    this.updateWalk();
  }

  private toggleAuto(): void {
    if (this.autoTimer != null) {
      this.stopAuto();
      return;
    }
    if (this.wt!.isDone) this.wt!.reset();
    this.setAutoLabel(true);
    this.autoTimer = window.setInterval(() => {
      if (!this.wt!.next()) this.stopAuto();
      this.updateWalk();
    }, 750);
  }

  private stopAuto(): void {
    if (this.autoTimer != null) {
      clearInterval(this.autoTimer);
      this.autoTimer = null;
    }
    this.setAutoLabel(false);
  }

  private setAutoLabel(running: boolean): void {
    const btn = [...this.mount.querySelectorAll(".sws-btn")].find((b) =>
      /Auto|Pause/.test(b.textContent || ""),
    );
    if (btn) btn.textContent = running ? "Pause" : "Auto-play";
  }

  private updateWalk(): void {
    const wt = this.wt!;
    const s = wt.current;
    this.hintCells.clear();
    this.roles = {};
    if (!s) {
      this.walkStepEl.textContent = "Ready";
      this.walkReasonEl.textContent = "Press Next to begin, or Auto-play to watch it solve.";
      this.walkDetailEl.textContent = "";
      this.walkLegendEl.replaceChildren();
      this.walkDefEl.replaceChildren();
      this.sel = null;
    } else {
      this.walkStepEl.replaceChildren(
        document.createTextNode(`Step ${wt.index} of ${wt.total} · `),
        this.techniqueLabel(s.technique, s.reason),
      );
      this.walkReasonEl.textContent = s.reason;
      this.walkDetailEl.textContent = s.detail || "";
      this.sel = s.cell ? idx(s.cell[0], s.cell[1]) : null;
      this.setRoles(s.highlights);
      this.renderLegend(s.highlights);
      this.showDef(s.technique);
    }
    if (wt.isDone && s) this.showStuckDone();
    else this.winEl.hidden = true;
    this.paint();
  }

  /** The per-role cell sets the painter highlights for the current step. */
  private setRoles(highlights: Highlight[]): void {
    this.roles = {};
    for (const hl of highlights) {
      const set = (this.roles[hl.role] ||= new Set<number>());
      for (const [r, c] of hl.cells) set.add(idx(r, c));
    }
  }

  /** A small legend naming each highlighted piece (so the colours are readable). */
  private renderLegend(highlights: Highlight[]): void {
    const LABEL: Record<string, string> = {
      target: "Target",
      cause: "Because",
      unit: "Context",
      line: "Line",
      eliminated: "Ruled out",
    };
    this.walkLegendEl.replaceChildren(
      ...highlights.map((hl) =>
        h("li", { class: `sws-legend-item role-${hl.role}` }, [
          h("span", { class: "sws-legend-dot" }),
          document.createTextNode(hl.note ? `${LABEL[hl.role] || hl.role}: ${hl.note}` : LABEL[hl.role] || hl.role),
        ]),
      ),
    );
  }

  private showStuckDone(): void {
    this.winEl.replaceChildren(
      h("h3", { text: "That's the full solve." }),
      h("p", { text: "Every letter placed by pure logic — no guessing. Want a hundred more like it?" }),
      h("div", { class: "hero-cta" }, [
        h("a", { class: "btn btn-amazon", href: AMAZON, rel: "nofollow noopener", target: "_blank", text: "Get the paperback" }),
        h("a", { class: "btn", href: "/play/", text: "Play free online" }),
      ]),
    );
    this.winEl.hidden = false;
  }

  // ---- shared ----
  private flashWord(word: string): void {
    const cells = this.puzzle.loops[word];
    if (!cells) return;
    this.hintCells = new Set(cells.map(([r, c]) => idx(r, c)));
    this.paint();
    window.setTimeout(() => {
      this.hintCells.clear();
      this.paint();
    }, 1400);
  }

  private setStatus(text: string, kind: "" | "ok" | "bad"): void {
    this.statusEl.textContent = text;
    this.statusEl.className = `sws-status${kind ? " " + kind : ""}`;
  }

  private paint(): void {
    const conflicts = this.board.conflicts();
    const found = this.board.foundWords();
    const sel = this.sel;
    const highlight = sel != null && this.board.value[sel] ? this.board.value[sel] : this.activeLetter;
    const peerSet = sel != null ? new Set(peers(sel)) : new Set<number>();

    for (let i = 0; i < 81; i++) {
      const cell = this.cells[i];
      const val = this.board.value[i];
      // content
      if (val) {
        if (cell.textContent !== val || cell.firstElementChild) cell.textContent = val;
      } else {
        const marks = this.pencilFor(i);
        const struck = this.struckFor(i);
        if (marks.size || struck.size) cell.replaceChildren(this.pencilEl(marks, struck));
        else if (cell.textContent || cell.firstElementChild) cell.textContent = "";
      }
      // dynamic state classes
      if (this.mode === "stuck") {
        // The current step's component pieces, by role.
        cell.classList.toggle("is-target", !!this.roles.target?.has(i));
        const bg = this.roles.eliminated?.has(i)
          ? "is-elim"
          : this.roles.line?.has(i)
            ? "is-line"
            : this.roles.cause?.has(i)
              ? "is-cause"
              : this.roles.unit?.has(i)
                ? "is-unit"
                : "";
        for (const k of ["is-elim", "is-line", "is-cause", "is-unit"]) cell.classList.toggle(k, k === bg);
        cell.classList.toggle("is-hint", this.hintCells.has(i)); // word flash
        for (const k of ["is-selected", "is-peer", "is-same", "is-conflict"]) cell.classList.toggle(k, false);
      } else {
        cell.classList.toggle("is-selected", i === sel);
        cell.classList.toggle("is-peer", i !== sel && peerSet.has(i));
        cell.classList.toggle("is-same", !!highlight && val === highlight && i !== sel);
        cell.classList.toggle("is-conflict", conflicts.has(i) || this.flaggedWrong.has(i));
        cell.classList.toggle("is-hint", this.hintCells.has(i));
      }
    }

    // Grey out a letter (in the alphabet and on the keypad) once all nine of it
    // are on the grid — a "used up" cue; the key stays usable.
    const counts: Record<string, number> = {};
    for (let i = 0; i < 81; i++) {
      const v = this.board.value[i];
      if (v) counts[v] = (counts[v] || 0) + 1;
    }
    for (const L of this.puzzle.alphabet) {
      const done = (counts[L] || 0) >= 9;
      this.rackEls.get(L)?.classList.toggle("is-complete", done);
      this.keyEls.get(L)?.classList.toggle("is-complete", done);
    }

    for (const [w, el] of this.wordEls) el.classList.toggle("is-found", found.has(w));
    this.paintLoops(found);

    if (this.mode === "play") {
      const allFound = found.size === this.puzzle.words.length && this.puzzle.words.length > 0;
      const t = this.statusEl;
      if (!this.won && t.className === "sws-status" && !t.textContent) {
        t.textContent = allFound ? "All words found — finish the grid!" : "";
      }
    }
  }

  /** Draw a rounded "pencil loop" around every found word, over the grid. The
   *  loop is a capsule outline (no fill) so the letters still read through it. */
  private paintLoops(found: Set<string>): void {
    const NS = "http://www.w3.org/2000/svg";
    const paths: SVGElement[] = [];
    // Flex/font centering renders each letter's ink ~0.016 cell below the cell's
    // geometric center, so a loop drawn through the exact centers reads a hair
    // high of the word. Nudge the loop down onto the ink (kept in sync with the
    // homepage hero's <g transform> so the showcase matches the live solver).
    const DY = 0.016;
    for (const w of found) {
      const cells = this.puzzle.loops[w];
      if (!cells || cells.length < 1) continue;
      const [r0, c0] = cells[0];
      const [r1, c1] = cells[cells.length - 1];
      const path = document.createElementNS(NS, "path");
      path.setAttribute("class", "sws-loop");
      path.setAttribute("d", this.loopPath(c0 + 0.5, r0 + 0.5 + DY, c1 + 0.5, r1 + 0.5 + DY));
      paths.push(path);
    }
    this.loopSvg.replaceChildren(...paths);
  }

  /** A stadium/capsule outline from (ax,ay) to (bx,by), in cell units (0..9). */
  private loopPath(ax: number, ay: number, bx: number, by: number): string {
    const R = 0.42;
    let dx = bx - ax;
    let dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    const px = -dy * R; // perpendicular offset (half-width)
    const py = dx * R;
    const f = (x: number, y: number) => `${x.toFixed(3)} ${y.toFixed(3)}`;
    // sweep-flag 0 so each semicircular cap bulges *outward* past the word end.
    return (
      `M ${f(ax + px, ay + py)} L ${f(bx + px, by + py)} ` +
      `A ${R} ${R} 0 0 0 ${f(bx - px, by - py)} L ${f(ax - px, ay - py)} ` +
      `A ${R} ${R} 0 0 0 ${f(ax + px, ay + py)} Z`
    );
  }

  /** Candidate letters to pencil in cell i: the player's marks in play mode, the
   *  walkthrough's computed candidates in stuck mode. */
  private pencilFor(i: number): Set<string> {
    if (this.mode === "stuck" && this.wt) return this.wt.candidates[i] || new Set();
    return this.board.pencil[i];
  }

  /** Candidates struck out (X'd) in cell i this step — the current prune's
   *  eliminations, in stuck mode only; gone on the next step. */
  private struckFor(i: number): Set<string> {
    const out = new Set<string>();
    if (this.mode === "stuck" && this.wt) {
      for (const e of this.wt.struck) if (idx(e.cell[0], e.cell[1]) === i) out.add(e.letter);
    }
    return out;
  }

  private pencilEl(marks: Set<string>, struck: Set<string>): HTMLElement {
    const wrap = h("div", { class: "sws-pencil" });
    for (const L of this.puzzle.alphabet) {
      if (struck.has(L)) wrap.append(h("span", { class: "struck", text: L }));
      else wrap.append(h("span", { text: marks.has(L) ? L : "" }));
    }
    return wrap;
  }

  /** The step's technique name as a plain inline label. Naked and hidden singles
   *  share one grading rung ("singles"), so name the specific kind from the
   *  reason — the hint then reads "Naked single" / "Hidden single". */
  private techniqueLabel(technique: string, reason?: string): HTMLElement {
    const info = TECHNIQUE_INFO[technique];
    let name = info ? info.title : technique.replace(/-/g, " ");
    if (technique === "singles" && reason) {
      if (/^hidden single/i.test(reason)) name = "Hidden single";
      else if (/^naked single/i.test(reason)) name = "Naked single";
    }
    return h("span", { class: "sws-tech-name", text: name });
  }

  /** Always show the technique's full (brief) definition under the step — the
   *  walkthrough never sends you elsewhere to learn what a move means. */
  private showDef(technique: string): void {
    const info = TECHNIQUE_INFO[technique];
    if (!info) {
      this.walkDefEl.replaceChildren();
      return;
    }
    this.walkDefEl.replaceChildren(
      h("h4", { class: "sws-def-title", text: info.title }),
      h("p", { class: "sws-def-body", text: info.summary }),
    );
  }
}
